// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import LocationData from '../../assets/files/location.json';

export default class LocationAPI {
  getCities() {
    return LocationData.provinces.map((o) => {
      const { id, name, area, altitude, areaCode, maps, region } = o;

      return { id, name, area, altitude, areaCode, maps, region };
    });
  }

  getProvinces(name: string, minPopulation = 1, maxPopulation = 1000000000, isMetropolitan = null, offset = 0, limit = 81, fields = null, sort = null) {
    try {
      // eslint-disable-next-line prefer-rest-params
      if (Object.values(arguments).some((item) => item)) {
        let provinces = LocationData.provinces;

        if (name) {
          const nameAlt = name.charAt(0).toLocaleUpperCase('TR') + name.slice(1).toLocaleLowerCase('tr');
          provinces = provinces.filter((item) => item.name.includes(name) || item.name.includes(nameAlt));
        }

        if (minPopulation || maxPopulation) {
          if (+minPopulation <= 0 && +maxPopulation <= 0) {
            throw {
              status: 404,
              message: "You can't search for a province with a population of 0 or less.",
            };
          }

          if (+minPopulation > +maxPopulation) {
            throw {
              status: 404,
              message: 'The minimum population cannot be greater than the maximum population.',
            };
          }

          provinces = provinces.filter((item) => {
            return item.population >= minPopulation && item.population <= maxPopulation;
          });
        }

        if (isMetropolitan) {
          if (isMetropolitan === 'true') {
            provinces = provinces.filter((item) => item.isMetropolitan === true);
          } else if (isMetropolitan === 'false') {
            provinces = provinces.filter((item) => item.isMetropolitan === false);
          } else {
            throw {
              status: 404,
              message: 'The isMetropolitan parameter must be either true or false.',
            };
          }
        }

        if (sort) {
          const sortArray = sort.split(',').reverse();
          const sortedProvinces = [];

          sortArray.forEach((item) => {
            if (item !== 'name' && item !== '-name') {
              if (item.startsWith('-')) {
                const field = item.slice(1);
                provinces.sort((a, b) => (a[field] > b[field] ? -1 : 1));
              } else {
                provinces.sort((a, b) => (a[item] > b[item] ? 1 : -1));
              }
            } else {
              if (item.startsWith('-')) {
                const field = item.slice(1);
                provinces.sort((a, b) => b[field].localeCompare(a[field], 'tr', { sensitivity: 'base' }));
              } else {
                provinces.sort((a, b) => a[item].localeCompare(b[item], 'tr', { sensitivity: 'base' }));
              }
            }
          });

          provinces.forEach((item) => {
            sortedProvinces.push(item);
          });

          provinces = sortedProvinces;

          if (
            sortArray.some((item) => !Object.keys(LocationData.provinces[0]).includes(item)) &&
            !sortArray.some((item) => !Object.keys(LocationData.provinces[0]).includes(item.startsWith('-')))
          ) {
            throw {
              status: 404,
              message: 'Invalid sort. The sort parameter must be a comma-separated list of valid fields.',
            };
          }
        }

        if (fields) {
          const fieldsArray = fields.split(',');
          const filteredProvinces = [];

          provinces.forEach((item) => {
            const filteredProvince = {};
            fieldsArray.forEach((field) => {
              filteredProvince[field] = item[field];
            });
            filteredProvinces.push(filteredProvince);
          });

          provinces = filteredProvinces;

          if (fieldsArray.some((item) => !Object.keys(LocationData.provinces[0]).includes(item))) {
            throw {
              status: 404,
              message: 'Invalid fields. The fields parameter must be a comma-separated list of valid fields.',
            };
          }
        }

        if (offset || limit) {
          provinces = provinces.slice(+offset, +offset + +limit);

          if (+offset >= LocationData.provinces.length && +limit == 0) {
            throw {
              status: 404,
              message: 'Invalid offset and limit. The limit value must be greater than 0. The offset value must be less than ' + LocationData.provinces.length,
            };
          } else if (+offset >= LocationData.provinces.length) {
            throw {
              status: 404,
              message: 'Invalid offset. The offset value must be less than ' + LocationData.provinces.length,
            };
          } else if (+limit == 0) {
            throw {
              status: 404,
              message: 'Invalid limit. The limit value must be greater than 0.',
            };
          }
        }

        if (provinces.length > 0) {
          return provinces;
        } else {
          throw {
            status: 404,
            message: 'No province found.',
          };
        }
      } else {
        return LocationData.provinces;
      }
    } catch (error) {
      throw {
        status: error?.status || 500,
        message: error?.message || 'Internal Server Error',
      };
    }
  }

  getExactProvince(id, fields) {
    try {
      if (!isFinite(id)) {
        throw {
          status: 404,
          message: 'Invalid province ID. The id parameter must be a number.',
        };
      }

      const province = LocationData.provinces[id - 1];

      if (fields) {
        const fieldsArray = fields.split(',');
        const filteredProvince = {};

        fieldsArray.forEach((field) => {
          filteredProvince[field] = province[field];
        });

        if (fieldsArray.some((item) => !Object.keys(LocationData.provinces[0]).includes(item))) {
          throw {
            status: 404,
            message: 'Invalid fields. The fields parameter must be a comma-separated list of valid fields.',
          };
        }
        return filteredProvince;
      } else {
        if (province) {
          return province;
        } else {
          throw {
            status: 404,
            message: 'No province found.',
          };
        }
      }
    } catch (error) {
      throw {
        status: error?.status || 500,
        message: error?.message || 'Internal Server Error',
      };
    }
  }

  getDistricts(name: string, minPopulation = 1, maxPopulation = 1000000000, offset = 0, limit = 976, fields = null, sort = null) {
    try {
      let districts = [];
      LocationData.provinces.forEach((province) => {
        province.districts.forEach((item) => {
          item.province = province.name;
        });
        districts = districts.concat(province.districts);
      });
      const initialDistricts = [...districts];

      // eslint-disable-next-line prefer-rest-params
      if (Object.values(arguments).some((item) => item)) {
        if (name) {
          const nameAlt = name.charAt(0).toLocaleUpperCase('TR') + name.slice(1).toLocaleLowerCase('tr');
          districts = districts.filter((item) => item.name.includes(name) || item.name.includes(nameAlt));
        }

        if (minPopulation || maxPopulation) {
          if (+minPopulation <= 0 && +maxPopulation <= 0) {
            throw {
              status: 404,
              message: "You can't search for a district with a population of 0 or less.",
            };
          }

          if (+minPopulation > +maxPopulation) {
            throw {
              status: 404,
              message: 'The minimum population cannot be greater than the maximum population.',
            };
          }

          districts = districts.filter((item) => {
            return item.population >= minPopulation && item.population <= maxPopulation;
          });
        }

        if (sort) {
          const sortArray = sort.split(',').reverse();
          const sortedDistricts = [];

          sortArray.forEach((item) => {
            if (item !== 'name' && item !== '-name' && item !== 'province' && item !== '-province') {
              if (item.startsWith('-')) {
                const field = item.slice(1);
                districts.sort((a, b) => (a[field] > b[field] ? -1 : 1));
              } else {
                districts.sort((a, b) => (a[item] > b[item] ? 1 : -1));
              }
            } else {
              if (item.startsWith('-')) {
                const field = item.slice(1);
                districts.sort((a, b) => b[field].localeCompare(a[field], 'tr', { sensitivity: 'base' }));
              } else {
                districts.sort((a, b) => a[item].localeCompare(b[item], 'tr', { sensitivity: 'base' }));
              }
            }
          });

          districts.forEach((item) => {
            sortedDistricts.push(item);
          });

          districts = sortedDistricts;

          if (sortArray.some((item) => !Object.keys(name).includes(item)) && !sortArray.some((item) => !Object.keys(name).includes(item.startsWith('-')))) {
            throw {
              status: 404,
              message: 'Invalid sort. The sort parameter must be a comma-separated list of valid fields.',
            };
          }
        }

        if (fields) {
          const fieldsArray = fields.split(',');
          const filteredDistricts = [];

          districts.forEach((item) => {
            const filteredProvince = {};
            fieldsArray.forEach((field) => {
              filteredProvince[field] = item[field];
            });
            filteredDistricts.push(filteredProvince);
          });

          districts = filteredDistricts;

          if (fieldsArray.some((item) => !Object.keys(districts[0]).includes(item))) {
            throw {
              status: 404,
              message: 'Invalid fields. The fields parameter must be a comma-separated list of valid fields.',
            };
          }
        }

        if (offset || limit) {
          districts = districts.slice(+offset, +offset + +limit);

          if (+offset >= initialDistricts.length && +limit == 0) {
            throw {
              status: 404,
              message: 'Invalid offset and limit. The limit value must be greater than 0. The offset value must be less than ' + initialDistricts.length,
            };
          } else if (+offset >= initialDistricts.length) {
            throw {
              status: 404,
              message: 'Invalid offset. The offset value must be less than ' + initialDistricts.length,
            };
          } else if (+limit == 0) {
            throw {
              status: 404,
              message: 'Invalid limit. The limit value must be greater than 0.',
            };
          }
        }
      }

      if (districts.length > 0) {
        return districts;
      } else {
        throw {
          status: 404,
          message: 'No districts found.',
        };
      }
    } catch (error) {
      throw {
        status: error?.status || 500,
        message: error?.message || 'Internal Server Error',
      };
    }
  }

  getExactDistrict(id, fields) {
    try {
      let districts = [];
      LocationData.provinces.forEach((province) => {
        province.districts.forEach((item) => {
          item.province = province.name;
        });
        districts = districts.concat(province.districts);
      });

      let fieldsArrayCopy;

      if (fields) {
        const fieldsArray = fields.split(',');
        fieldsArrayCopy = [...fieldsArray];

        // BUG: The program cannot find the exact district when the user does not add "id" to the fields query. So I added a workaround.
        if (fieldsArray.every((item) => item !== 'id')) fieldsArray.push('id');
        // BUG: The program cannot find the exact district when the user does not add "id" to the fields query. So I added a workaround.

        const filteredDistricts = [];

        districts.forEach((item) => {
          const filteredProvince = {};
          fieldsArray.forEach((field) => {
            filteredProvince[field] = item[field];
          });
          filteredDistricts.push(filteredProvince);
        });

        districts = filteredDistricts;

        if (fieldsArray.some((item) => !Object.keys(districts[0]).includes(item))) {
          throw {
            status: 404,
            message: 'Invalid fields. The fields parameter must be a comma-separated list of valid fields.',
          };
        }
      }

      const district = districts.find((item) => item.id === +id);
      if (fieldsArrayCopy && !fieldsArrayCopy.includes('id')) district.id = undefined; // a temporary solution to the BUG I mentioned above.

      if (district) {
        return district;
      } else {
        throw {
          status: 404,
          message: 'No district found.',
        };
      }
    } catch (error) {
      throw {
        status: error?.status || 500,
        message: error?.message || 'Internal Server Error',
      };
    }
  }
}
